import { usePage } from "@inertiajs/react"
import { useCallback, useEffect, useState } from "react"

import type { SharedProps } from "@/types"

export type Appearance = "light" | "dark" | "system"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  const html = document.documentElement
  // Server-locked themes (partner orgs) must not be overridden by user
  // preference — prevents a flash when initializeTheme() / useAppearance()
  // run on mount and temporarily flip the class back.
  if (html.dataset.themeLocked !== undefined) return

  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  html.classList.toggle("dark", isDark)
  html.style.colorScheme = isDark ? "dark" : "light"
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const handleSystemThemeChange = () => {
  const currentAppearance = localStorage.getItem("appearance") as Appearance
  applyTheme(currentAppearance ?? "system")
}

export function initializeTheme() {
  const savedAppearance =
    (localStorage.getItem("appearance") as Appearance) || "system"

  applyTheme(savedAppearance)

  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>(() => {
    if (typeof window === "undefined") return "system"
    const saved = localStorage.getItem("appearance") as Appearance | null
    return saved ?? "system"
  })

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode)
    if (mode === "system") {
      localStorage.removeItem("appearance")
    } else {
      localStorage.setItem("appearance", mode)
    }
    applyTheme(mode)
  }, [])

  useEffect(() => {
    applyTheme(appearance)

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [appearance])

  return { appearance, updateAppearance } as const
}

/**
 * Applies the organization's theme for partner/whitelabel orgs only.
 * Non-partner orgs and pages without org context use the user's
 * personal preference (restored on cleanup).
 */
export function useOrgTheme() {
  const { props } = usePage<SharedProps>()
  const org = props.organization

  useEffect(() => {
    const html = document.documentElement
    // Only override theme for partner orgs with an explicit theme
    if (org?.partner && org.theme === "dark") {
      // Apply first, then lock — otherwise applyTheme short-circuits on the
      // lock it just set (matters on SPA nav into a partner org from a
      // non-partner page where the server didn't render the class).
      applyTheme("dark")
      html.dataset.themeLocked = ""
      return () => {
        delete html.dataset.themeLocked
        // Restore user preference when leaving a partner org
        const saved =
          (localStorage.getItem("appearance") as Appearance) || "system"
        applyTheme(saved)
      }
    }
  }, [org?.slug, org?.partner, org?.theme])
}
